import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Package, BookOpen, Ruler, GitMerge, BellRing, Check, Download, ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { SemanticsTabs } from "@/components/semantics/SemanticsTabs";
import { useWorkspace } from "@/context/WorkspaceContext";
import { SEMANTICS } from "@/constants/testIds";
import api from "@/lib/api";

const SECTOR_BADGE = {
  SaaS: "border-violet-500/40 text-violet-300 bg-violet-500/10",
  Healthcare: "border-emerald-500/40 text-emerald-400 bg-emerald-500/10",
  FinServ: "border-sky-500/40 text-sky-400 bg-sky-500/10",
  Retail: "border-pink-500/40 text-pink-400 bg-pink-500/10",
  Logistics: "border-amber-500/40 text-amber-400 bg-amber-500/10",
  Manufacturing: "border-orange-500/40 text-orange-400 bg-orange-500/10",
  Other: "border-slate-500/40 text-slate-300 bg-slate-500/10",
};

const CONTRIB_META = [
  { key: "glossary", label: "terms", icon: BookOpen },
  { key: "metrics", label: "metrics", icon: Ruler },
  { key: "joins", label: "joins", icon: GitMerge },
  { key: "alerts", label: "alerts", icon: BellRing },
];

export default function SemanticsPacks() {
  const { active } = useWorkspace();
  const isAdmin = ["owner", "admin"].includes(active?.role);
  const [packs, setPacks] = useState(null);
  const [installed, setInstalled] = useState({});
  const [installing, setInstalling] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    if (!active) return;
    const [cat, inst] = await Promise.all([
      api.get("/packs"),
      api.get("/packs/installed", { params: { workspace_id: active.id } }),
    ]);
    setPacks(cat.data);
    const map = {};
    inst.data.forEach((p) => { map[p.slug] = p; });
    setInstalled(map);
  }, [active]);

  useEffect(() => { load(); }, [load]);

  const install = async (slug) => {
    setInstalling(slug);
    try {
      const { data } = await api.post("/packs/install", { workspace_id: active.id, slug });
      const added = data.added || {};
      toast.success(
        data.reinstall
          ? `${data.name} re-synced`
          : `Installed ${data.name} — ${added.glossary || 0} terms, ${added.metrics || 0} metrics`
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Install failed");
    } finally {
      setInstalling(null);
    }
  };

  const openDetail = async (pack) => {
    setDetailLoading(true);
    setDetail(pack); // show name/description/contributions immediately
    try {
      const { data } = await api.get(`/packs/${pack.slug}`);
      setDetail((prev) => ({ ...prev, ...data }));
    } catch {
      toast.error("Could not load pack");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div data-testid={SEMANTICS.packsRoot} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-6xl px-6 py-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Semantic packs</h1>
              <SemanticsTabs active="packs" />
            </div>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              Industry vocabularies that teach Sema your sector's language. Installing a pack seeds
              draft glossary terms and metrics you can promote — plus common join and alert templates.
            </p>
          </div>
        </div>

        {packs === null ? (
          <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading packs…
          </div>
        ) : (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {packs.map((p) => {
              const isInstalled = !!installed[p.slug];
              const busy = installing === p.slug;
              return (
                <div
                  key={p.slug}
                  data-testid={SEMANTICS.packCard}
                  className="flex flex-col rounded-2xl border border-border/60 bg-card/40 p-5 transition-colors hover:border-accent/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border/60 bg-background">
                      <Package className="h-5 w-5 text-accent" />
                    </div>
                    {isInstalled ? (
                      <Badge data-testid={SEMANTICS.packInstalledBadge} variant="outline" className="rounded-full border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] text-emerald-400">
                        <Check className="mr-1 h-3 w-3" /> Installed
                      </Badge>
                    ) : (
                      <Badge variant="outline" className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] ${SECTOR_BADGE[p.domain] || SECTOR_BADGE.Other}`}>
                        {p.domain}
                      </Badge>
                    )}
                  </div>

                  <h2 className="mt-4 text-lg font-semibold tracking-tight">{p.name}</h2>
                  <p className="mt-1 line-clamp-3 flex-1 text-xs leading-relaxed text-muted-foreground">{p.description}</p>

                  <div className="mt-4 grid grid-cols-4 gap-2">
                    {CONTRIB_META.map(({ key, label, icon: Icon }) => (
                      <div key={key} className="flex flex-col items-center rounded-lg border border-border/40 bg-background/60 py-2">
                        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="mt-1 text-sm font-semibold text-foreground">{p.contributions?.[key] ?? 0}</span>
                        <span className="text-[9px] uppercase tracking-[0.1em] text-muted-foreground">{label}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 flex items-center gap-2">
                    <Button
                      data-testid={SEMANTICS.packInstall}
                      size="sm"
                      disabled={!isAdmin || busy || isInstalled}
                      onClick={() => install(p.slug)}
                      className="flex-1 rounded-full bg-accent text-xs uppercase tracking-[0.12em] text-accent-foreground hover:bg-accent/90 disabled:opacity-60"
                    >
                      {busy ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : isInstalled ? <Check className="mr-2 h-3.5 w-3.5" /> : <Download className="mr-2 h-3.5 w-3.5" />}
                      {isInstalled ? "Installed" : "Install"}
                    </Button>
                    <Button
                      data-testid={SEMANTICS.packView}
                      size="sm"
                      variant="outline"
                      onClick={() => openDetail(p)}
                      className="rounded-full border-border/70 text-xs uppercase tracking-[0.12em]"
                    >
                      View
                    </Button>
                  </div>
                  {!isAdmin && !isInstalled && (
                    <p className="mt-2 text-[10px] text-muted-foreground">Ask an admin to install this pack.</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      <PackDetailDialog
        detail={detail}
        loading={detailLoading}
        onClose={() => setDetail(null)}
        installed={detail && !!installed[detail.slug]}
        isAdmin={isAdmin}
        installing={installing === detail?.slug}
        onInstall={() => install(detail.slug)}
      />
    </div>
  );
}

function Section({ title, icon: Icon, items, render }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
        <Icon className="h-3.5 w-3.5 text-accent" /> {title} <span className="text-accent">{items.length}</span>
      </div>
      <div className="mt-2 space-y-1.5">{items.map(render)}</div>
    </div>
  );
}

function PackDetailDialog({ detail, loading, onClose, installed, isAdmin, installing, onInstall }) {
  const open = !!detail;
  const d = detail || {};
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid={SEMANTICS.packDetailRoot} className="max-h-[85vh] max-w-2xl overflow-y-auto font-mono">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Package className="h-5 w-5 text-accent" /> {d.name || "Pack"}
            {installed && <Badge variant="outline" className="rounded-full border-emerald-500/40 bg-emerald-500/10 text-[10px] text-emerald-400">Installed</Badge>}
          </DialogTitle>
          <DialogDescription className="text-xs">{d.description}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-accent" /></div>
        ) : (
          <div className="space-y-5 pt-2">
            <Section
              title="Glossary terms" icon={BookOpen} items={d.glossary_terms}
              render={(t) => (
                <div key={t.name} className="rounded-lg border border-border/50 bg-card/40 px-3 py-2">
                  <span className="text-sm font-semibold">{t.name}</span>
                  <p className="text-xs text-muted-foreground">{t.definition}</p>
                </div>
              )}
            />
            <Section
              title="Metrics" icon={Ruler} items={d.metric_candidates}
              render={(m) => (
                <div key={m.name} className="rounded-lg border border-border/50 bg-card/40 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{m.name}</span>
                    <Badge variant="outline" className="rounded-full px-1.5 py-0 text-[9px] text-muted-foreground">{m.domain}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{m.definition}</p>
                </div>
              )}
            />
            <Section
              title="Join templates" icon={GitMerge} items={d.join_templates}
              render={(j) => (
                <div key={j.name} className="rounded-lg border border-border/50 bg-card/40 px-3 py-2 text-xs">
                  <span className="font-semibold text-foreground">{j.name}</span>
                  <span className="ml-2 text-muted-foreground">{j.from_table}.{j.from_column} → {j.to_table}.{j.to_column}</span>
                </div>
              )}
            />
            <Section
              title="Alert templates" icon={BellRing} items={d.alert_templates}
              render={(a) => (
                <div key={a.name} className="rounded-lg border border-border/50 bg-card/40 px-3 py-2 text-xs">
                  <span className="font-semibold text-foreground">{a.name}</span>
                  <span className="ml-2 text-muted-foreground">{a.metric} · {a.method} · {a.severity}</span>
                </div>
              )}
            />

            {isAdmin && !installed && (
              <Button onClick={onInstall} disabled={installing} className="w-full rounded-full bg-accent text-xs uppercase tracking-[0.14em] text-accent-foreground hover:bg-accent/90">
                {installing ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-2 h-3.5 w-3.5" />} Install this pack
                <ArrowRight className="ml-2 h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
